import React, { useEffect, useId, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useWatch, type UseFormReturn } from "react-hook-form";
import { CTextFieldIdentifyName } from "@/components/TextField/CTextFieldIdentifyName";
import { CTextFieldPastDays } from "@/components/TextField/CTextFieldPastDays";
import {
  SizingWrapper,
  SizingWrapperStyle,
} from "@/components/Wrapper/SizingWrapper";
import {
  LEGACY_POLICY,
  POLICY_PRESETS,
  matchPreset,
  STEP_COST,
  summarizePolicy,
  type AcquireStep,
  type AcquireMethod,
} from "../utils/acquirePolicy";
import {
  fieldsFromPolicy,
  isExcludeAcquiredLocked,
  POLICY_FIELD_NAMES,
  type BulkAcquireFormData,
  type PolicyFields,
} from "../utils/bulkAcquireFormSchema";
import { usePolicyFromForm } from "../hooks/useBulkAcquireForm";
import { BulkExcludeAcquiredCheckbox } from "./BulkExcludeAcquiredCheckbox";

const CUSTOM = "CUSTOM";
type PresetValue = AcquireMethod | typeof CUSTOM;
type Mode = BulkAcquireFormData["mode"];

/** 取得方法セレクトの選択肢（表示順）。POLICY_PRESETS の5方式＋詳細で組む「カスタム」 */
const ACQUIRE_METHOD_OPTIONS: Array<{ value: PresetValue; label: string }> = [
  { value: "NEW_ONLY", label: "全てを新規取得" },
  { value: "REUSE_PRIORITY", label: "リユース登記を優先して取得" },
  { value: "REUSE_ONLY", label: "リユース登記のみを取得" },
  { value: "ACQUIRED_PRIORITY", label: "自社取得済み登記を優先して取得" },
  { value: "ACQUIRED_ONLY", label: "自社取得済み登記のみを取得" },
  { value: CUSTOM, label: "カスタム" },
];

/** 方式を切り替えた直後の条件。経過日数を指定する方式は取得方法を選ばず LEGACY_POLICY で固定する */
const DEFAULT_FIELDS_BY_MODE: Record<Mode, PolicyFields> = {
  legacy: fieldsFromPolicy(LEGACY_POLICY),
  policy: fieldsFromPolicy(POLICY_PRESETS.REUSE_PRIORITY),
};

const STEP_FIELD = {
  acquired: { name: "useAcquired", label: "自社取得済み登記" },
  reuse: { name: "useReuse", label: "リユース登記" },
  new: { name: "useNew", label: "新規取得" },
} as const;

/** 優先順位の1段。料金区分があれば本文より一段小さい注釈として添える */
const CStepCheckbox: React.FC<{
  form: UseFormReturn<BulkAcquireFormData>;
  step: AcquireStep;
}> = ({ form, step }) => {
  const { name, label } = STEP_FIELD[step];
  const cost = STEP_COST[step];
  return (
    <Controller
      name={name}
      control={form.control}
      render={({ field: { name: fieldName, value, onChange } }) => (
        <FormControlLabel
          sx={{ mx: 0 }}
          control={
            <Checkbox
              sx={{ padding: "0px 4px" }}
              checked={value}
              onChange={(event) => {
                onChange(event.target.checked);
              }}
              name={fieldName}
            />
          }
          label={
            <>
              {label}
              {cost != null && (
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  {cost}
                </Typography>
              )}
            </>
          }
        />
      )}
    />
  );
};

/* 入力欄と同じ行に置く箱。見出し行の分だけ下げ、入力欄（56px）の中心にチェックボックスをそろえる。
   非活性時の補足文はこの箱の中で下に伸びるので左の欄の位置は動かない */
const CHECKBOX_SLOT_SX = {
  pt: 4,
  minHeight: 56,
  display: "flex",
  alignItems: "center",
} as const;

const CONDITION_ROW_SX = {
  display: "flex",
  gap: 4,
  alignItems: "flex-start",
  flexWrap: "wrap",
} as const;

interface BulkAcquireFormProps {
  form: UseFormReturn<BulkAcquireFormData>;
  /** 経過日数の入力中を画面側へ伝える（打鍵途中の値でチェックを解除させないため） */
  pastDaysFocusProps?: {
    onFocus: () => void;
    onBlur: () => void;
  };
}

/**
 * 識別名と取得条件の入力欄。取得条件は従来方式（経過日数だけを指定し、自社取得済み → リユース → 新規の順に使う）と
 * 新方式（経過日数を使わず取得方法を選び、カスタムでは使う登記の優先順位を組む）をラジオで切り替える。
 * どちらの方式もフォームの取得条件4項目に写し、セレクトの表示はその値から導く
 */
export const BulkAcquireForm: React.FC<BulkAcquireFormProps> = ({
  form,
  pastDaysFocusProps,
}) => {
  const { fields, policy } = usePolicyFromForm(form.control);
  const { useAcquired, useReuse, useNew, ignoreWindow } = fields;
  const mode = useWatch({ control: form.control, name: "mode" });
  // 値の解除は useBulkAcquireConditions が行う。ここは非活性の表示だけ
  const excludeAcquiredLocked = isExcludeAcquiredLocked(mode, policy);
  const labelId = useId();
  const errorId = useId();

  // カスタムは取得方法を選び直すまで維持する（詳細を触った結果がたまたま5方式のどれかと一致しても
  // 詳細を消さない。消えると操作の途中で編集欄が失われる）
  const [customSelected, setCustomSelected] = useState(false);
  // 「1つ以上選ぶ」は3項目にまたがるが、React Hook Form は変更した項目しか再検証しないため、
  // どれかが変わったら再検証してエラー表示を追従させる
  useEffect(() => {
    void form.trigger("useNew");
  }, [useAcquired, useReuse, useNew, form]);
  // 経過日数の範囲は方式（ignoreWindow）に依存する。新方式では欄が消えるが値は残るため、
  // 従来方式へ戻したときにエラー表示を復帰させる
  useEffect(() => {
    void form.trigger("pastDays");
  }, [ignoreWindow, form]);

  const preset: PresetValue = customSelected
    ? CUSTOM
    : matchPreset(policy) ?? CUSTOM;
  const stepsError = form.formState.errors.useNew?.message;

  const applyFields = (targetFields: PolicyFields): void => {
    setCustomSelected(false);
    for (const name of POLICY_FIELD_NAMES) {
      form.setValue(name, targetFields[name], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  // 方式ごとに探索順・経過日数の有無が違うため、切り替え時にその方式の既定へ戻す
  const handleModeChange = (next: Mode): void => {
    form.setValue("mode", next, { shouldDirty: true });
    applyFields(DEFAULT_FIELDS_BY_MODE[next]);
  };

  const handlePresetChange = (value: PresetValue): void => {
    if (value === CUSTOM) {
      setCustomSelected(true);
      return;
    }
    applyFields(fieldsFromPolicy(POLICY_PRESETS[value]));
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ width: 640, maxWidth: "100%" }}>
        <Typography sx={{ fontWeight: "bold", mb: 1 }}>識別名</Typography>
        <CTextFieldIdentifyName
          control={form.control}
          name="identifyName"
          size={SizingWrapperStyle.INHERIT}
        />
      </Box>
      {/* 方式の選択・入力欄・未取得のみ・カスタムの詳細を1つの「取得条件」として枠でまとめる
          （未取得のみを離れた場所に置くと存在に気づかれにくい） */}
      <Box
        component="fieldset"
        sx={{
          mt: 2,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          minWidth: 0,
          pt: 0.5,
          pb: 2,
          px: 2,
        }}
      >
        {/* 見出しは枠線に載せる（fieldset の legend） */}
        <Typography component="legend" sx={{ fontWeight: "bold", px: 0.5 }}>
          取得条件
        </Typography>
        <RadioGroup
          row
          aria-label="取得条件の指定方法"
          value={mode}
          onChange={(_, value) => {
            handleModeChange(value as Mode);
          }}
          sx={{ mb: 1 }}
        >
          <FormControlLabel
            value="policy"
            control={<Radio />}
            label="取得方法を指定して取得する"
          />
          <FormControlLabel
            value="legacy"
            control={<Radio />}
            label="経過日数を指定して取得する"
          />
        </RadioGroup>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {
            "自社取得済み登記: ログイン中のアカウントで取得済みの登記をそのまま使います（会社等でアカウント同士の共有設定がある場合は共有範囲のアカウントの取得分を含む）"
          }
          <br />
          {
            "リユース登記: R.E.DATA Plus上で取得された登記を新規取得より安く取得します"
          }
        </Typography>
        {/* 方式で入れ替わるのは左の入力欄だけ（従来は経過日数、新方式は取得方法） */}
        <Box sx={CONDITION_ROW_SX}>
          {mode === "legacy" ? (
            <Box {...pastDaysFocusProps}>
              <Typography sx={{ fontWeight: "bold", mb: 1 }}>
                経過日数{" "}
                <Typography component="span" sx={{ color: "error.main" }}>
                  *必須
                </Typography>
              </Typography>
              <CTextFieldPastDays
                control={form.control}
                name="pastDays"
                // 共通部品の既定文言は取得済登記にしか触れていないため、この画面ではリユース登記も補う
                tooltipText={
                  <>
                    取得済の場合でも経過日数を過ぎたものは再取得いたします
                    <br />
                    リユース登記も経過日数を過ぎたものは利用されません
                  </>
                }
              />
            </Box>
          ) : (
            <Box>
              <Typography sx={{ fontWeight: "bold", mb: 1 }}>
                取得方法
              </Typography>
              {/* 既定幅（250px）では「リユース登記を優先して取得」が末尾省略されるため広げる */}
              <SizingWrapper size={SizingWrapperStyle.LARGE}>
                <TextField
                  select
                  label="取得方法を選択する"
                  value={preset}
                  onChange={(event) => {
                    handlePresetChange(event.target.value as PresetValue);
                  }}
                  fullWidth
                >
                  {ACQUIRE_METHOD_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </SizingWrapper>
            </Box>
          )}
          <Box sx={CHECKBOX_SLOT_SX}>
            <BulkExcludeAcquiredCheckbox
              control={form.control}
              locked={excludeAcquiredLocked}
            />
          </Box>
        </Box>
        {mode === "policy" && preset === CUSTOM && (
          <Box sx={{ mt: 2 }}>
            <Typography
              id={labelId}
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              取得する登記の優先順位（左から順に探し、見つかった時点で確定）:{" "}
              {summarizePolicy(policy)}
            </Typography>
            <Box
              role="group"
              aria-labelledby={labelId}
              aria-describedby={stepsError != null ? errorId : undefined}
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              <CStepCheckbox form={form} step="acquired" />
              <Typography color="text.secondary">→</Typography>
              <CStepCheckbox form={form} step="reuse" />
              <Typography color="text.secondary">→</Typography>
              <CStepCheckbox form={form} step="new" />
            </Box>
            {stepsError != null && (
              <FormHelperText id={errorId} error role="alert">
                {stepsError}
              </FormHelperText>
            )}
            {!useNew && stepsError == null && (
              <FormHelperText>
                新規取得しないため、候補のない書類は依頼から除かれます
              </FormHelperText>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
